import useAuthStore from '@/store/useAuthStore'

/**
 * `fetch()` wrapper for streaming endpoints.
 *
 * Axios buffers the whole response body in the browser, so the NDJSON cash flow
 * feed has to go through `fetch`. This reproduces the parts of the shared
 * `httpClient` config that matter for an authenticated GET — base URL, Sanctum
 * cookies, the CSRF header and the team header — and mirrors the interceptor's
 * 401/419 handling since a raw fetch bypasses it.
 *
 * @param {string} path                 API path relative to /api/v1 (leading slash optional)
 * @param {Record<string, any>} [params] query string params (undefined values dropped)
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<Response>}          a Response whose body is an unread stream
 */
export async function streamRequest(path, params = {}, options = {}) {
  const doFetch = () =>
    fetch(buildUrl(path, params), {
      method: 'GET',
      credentials: 'include',
      headers: buildHeaders(),
      signal: options.signal,
    })

  let response = await doFetch()

  // CSRF token expired — refresh the cookie once and retry, same as the axios interceptor.
  if (response.status === 419) {
    await fetch(buildUrl('/csrf-cookie'), { credentials: 'include' })
    response = await doFetch()
  }

  if (response.status === 401) {
    useAuthStore.setState({ isAuthenticated: false })
    setTimeout(() => {
      window.location.href = '/login'
    }, 1000)
    throw new Error('Unauthenticated')
  }

  if (!response.ok) {
    throw new Error(`Stream request failed: ${response.status}`)
  }

  return response
}

function buildUrl(path, params = {}) {
  const base = `${import.meta.env.VITE_API_URL}/api/v1`
  const normalized = path.startsWith('/') ? path : `/${path}`
  const query = new URLSearchParams()

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.append(key, value)
    }
  })

  const suffix = query.toString()
  return suffix ? `${base}${normalized}?${suffix}` : `${base}${normalized}`
}

function buildHeaders() {
  const headers = {
    Accept: 'application/x-ndjson',
    'X-Requested-With': 'XMLHttpRequest',
  }

  const teamId = useAuthStore.getState().currentTeamId
  if (teamId) {
    headers['X-Team'] = String(teamId)
  }

  const xsrfToken = readCookie('XSRF-TOKEN')
  if (xsrfToken) {
    headers['X-XSRF-TOKEN'] = decodeURIComponent(xsrfToken)
  }

  return headers
}

function readCookie(name) {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'))
  return match ? match[2] : null
}
